// src/app/auth/auth.service.ts
import { Injectable, signal, computed, WritableSignal, Signal } from '@angular/core';
import { SignalStoreService } from './signal-store.service';
import { ApiService } from './api.service';

export interface User {
    id: string;
    name: string;
    email: string;
    userId?: string;
    employeeId?: string;
    roles?: string[];
    permissions?: string[];
}

type PersistentSignalReturn<T> = ReturnType<SignalStoreService['persistentSignal']>

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _user!: WritableSignal<User | null>;
    public user!: Signal<User | null>;
    private _token!: WritableSignal<String | null>;
    public token!: Signal<String | null>;
    readonly isLoggedIn = computed(() => this._user() !== null);
    currentUser: any;

    constructor(
        private signalStore: SignalStoreService,
        private apiService: ApiService
    ) {
        this._user = this.signalStore.persistentSignal<User | null>('auth_user', null);
        this.user = this._user.asReadonly();
        this._token = this.signalStore.persistentSignal('token', null);
        this.token = this._token.asReadonly();
    }

    login(email: string, password: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let request = {
                "username": email,
                "password": password
            }
            this.apiService.post("api/auth/login", request).subscribe({
                next: (response: any) => {
                    console.log(response)
                    if(response?.token) {
                        this._token.set(response?.token);

                        // Set user with role information
                        const user: User = {
                            id: response.userId || '1',
                            name: response.username || 'User',
                            email: email,
                            userId: response.userId,
                            employeeId: response.employeeId,
                            roles: response.roles || [],
                            permissions: this.extractPermissionsFromToken(response.token)
                        };

                        this._user.set(user);
                        resolve(true)
                    }
                    else {
                        resolve(false);
                    }
                },
                error: (error) => {
                    console.error('Login error:', error);
                    reject(error);
                }
            })
        });
    }

    logout(): void {
        this._user.set(null);
        this._token.set(null);
    }

    // Role and Permission checking methods
    hasRole(role: string): boolean {
        const user = this._user();
        return user?.roles?.includes(role) || false;
    }

    hasPermission(permission: string): boolean {
        const user = this._user();
        return user?.permissions?.includes(permission) || false;
    }

    hasAnyRole(roles: string[]): boolean {
        const user = this._user();
        if (!user?.roles) return false;
        return roles.some(role => user.roles!.includes(role));
    }

    hasAnyPermission(permissions: string[]): boolean {
        const user = this._user();
        if (!user?.permissions) return false;
        return permissions.some(permission => user.permissions!.includes(permission));
    }

    isAuthenticated(): boolean {
        const user = this._user();
        const token = this._token();

        // Check if both user and token exist
        if (!user || !token) {
            return false;
        }

        // Check if token is not empty string
        if (typeof token === 'string' && token.trim() === '') {
            return false;
        }

        // TODO: Add token expiration check here if needed
        return true;
    }

    getUserRoles() {
        return this._user()?.roles || [];
    }

    getCurrentUser() {
        return this._user();
    }

    // Debug method to check authentication state
    debugAuthState() {
        const user = this._user();
        const token = this._token();
        console.log('🔍 Auth Debug State:', {
            user: user,
            token: token ? 'Present' : 'Missing',
            tokenLength: token ? token.length : 0,
            isLoggedIn: this.isLoggedIn(),
            isAuthenticated: this.isAuthenticated(),
            roles: user?.roles || [],
            permissions: user?.permissions || []
        });
        return {
            hasUser: !!user,
            hasToken: !!token,
            isLoggedIn: this.isLoggedIn(),
            isAuthenticated: this.isAuthenticated(),
            roles: user?.roles || [],
            permissions: user?.permissions || []
        };
    }

    // Role constants
    static readonly ROLES = {
        HR_ADMIN: 'HR_ADMIN',
        MANAGER: 'MANAGER',
        EMPLOYEE: 'EMPLOYEE'
    };

    // Permission constants
    static readonly PERMISSIONS = {
        VIEW_ALL_EMPLOYEES: 'VIEW_ALL_EMPLOYEES',
        VIEW_OWN_PROFILE: 'VIEW_OWN_PROFILE',
        EDIT_ALL_EMPLOYEES: 'EDIT_ALL_EMPLOYEES',
        EDIT_OWN_PROFILE: 'EDIT_OWN_PROFILE',
        CREATE_EMPLOYEES: 'CREATE_EMPLOYEES',
        DELETE_EMPLOYEES: 'DELETE_EMPLOYEES',
        
        // Performance Management Permissions
        VIEW_ALL_PERFORMANCE: 'VIEW_ALL_PERFORMANCE',
        VIEW_OWN_PERFORMANCE: 'VIEW_OWN_PERFORMANCE',
        CREATE_PERFORMANCE_REVIEWS: 'CREATE_PERFORMANCE_REVIEWS',
        EDIT_PERFORMANCE_REVIEWS: 'EDIT_PERFORMANCE_REVIEWS',
        APPROVE_PERFORMANCE_REVIEWS: 'APPROVE_PERFORMANCE_REVIEWS',
        DELETE_PERFORMANCE_REVIEWS: 'DELETE_PERFORMANCE_REVIEWS',
        CREATE_PERFORMANCE_GOALS: 'CREATE_PERFORMANCE_GOALS',
        EDIT_PERFORMANCE_GOALS: 'EDIT_PERFORMANCE_GOALS',
        DELETE_PERFORMANCE_GOALS: 'DELETE_PERFORMANCE_GOALS',
        VIEW_PERFORMANCE_ANALYTICS: 'VIEW_PERFORMANCE_ANALYTICS'
    };

    // Computed properties for common role checks
    readonly isHRAdmin = computed(() => this.hasRole(AuthService.ROLES.HR_ADMIN));
    readonly isManager = computed(() => this.hasRole(AuthService.ROLES.MANAGER));
    readonly isEmployee = computed(() => this.hasRole(AuthService.ROLES.EMPLOYEE));

    // Extract permissions from JWT token
    private extractPermissionsFromToken(token: string): string[] {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.permissions || [];
        } catch (error) {
            console.error('Error extracting permissions from token:', error);
            return [];
        }
    }
}
